import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import TextField from '@mui/material/TextField'
import { IIncidentReportFormData } from '../IncidentReportModal'
import PrimaryDropdown from '../../../../global/components/dropdowns/PrimaryDropdown'
import PrimaryCheckbox from '../../../../global/components/checkbox/Checkbox'
import {
  INCIDENT_TYPES_ARRAY,
  REVIEW_CHANNELS_ARRAY,
  SEVERITY_OF_INCIDENT_ARRAY,
  INCIDENT_REPORT_STATUS_ARRAY,
} from '../../../../global/constants/incident-report'
import { formatStatus, formatIncidentType } from '../../../utils/format-status'
import styles from '../styles.module.css'
import { fetchData } from '../../../../global/utils/fetch'
import { getActiveTechnicians } from '../../../api'
import { ITechnician } from '../../../../../../app/entities/Technician'

interface IOverviewTabProps {
  formData: IIncidentReportFormData
  setFormData: Dispatch<SetStateAction<IIncidentReportFormData>>
}

function OverviewTab({ formData, setFormData }: IOverviewTabProps) {
  const [technicians, setTechnicians] = useState<ITechnician[]>([])

  useEffect(() => {
    async function fetchTechnicians() {
      try {
        const response = await fetchData<{ items: ITechnician[]; count: number }>(
          getActiveTechnicians(),
        )
        setTechnicians(response?.items || [])
      } catch (error) {
        console.error('Error fetching technicians:', error)
      }
    }
    fetchTechnicians()
  }, [])

  function handleChange(field: keyof IIncidentReportFormData, value: any) {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  function handleCheckboxChange(field: keyof IIncidentReportFormData, checked: boolean) {
    setFormData((prev) => ({ ...prev, [field]: checked }))
  }

  const incidentTypeItems = INCIDENT_TYPES_ARRAY.map((type) => ({
    key: type,
    value: formatIncidentType(type),
  }))

  const severityItems = SEVERITY_OF_INCIDENT_ARRAY.map((severity) => ({
    key: severity,
    value: severity,
  }))

  const reviewChannelItems = REVIEW_CHANNELS_ARRAY.map((channel) => ({
    key: channel,
    value: channel.replace(/_/g, ' '),
  }))

  const technicianItems = technicians.map((tech) => ({
    key: tech.objectId,
    value: tech.name,
  }))

  const statusItems = INCIDENT_REPORT_STATUS_ARRAY.map((status) => ({
    key: status,
    value: formatStatus(status),
  }))

  return (
    <div className={styles.formContainer}>
      <div className={styles.formRow}>
        <TextField
          label='Work Order ID'
          value={formData.workOrderId || 'N/A'}
          disabled
          InputLabelProps={{ shrink: true }}
          sx={{ flex: 1 }}
        />
        <div style={{ flex: 1 }}>
          <PrimaryDropdown
            label='Technician'
            defaultValue={formData.technicianObjectId || ''}
            onChange={(value) => handleChange('technicianObjectId', value)}
            items={technicianItems}
          />
        </div>
        <div style={{ flex: 1 }}>
          <PrimaryDropdown
            label='Status'
            defaultValue={formData.status}
            onChange={(value) => handleChange('status', value)}
            items={statusItems}
          />
        </div>
      </div>

      <div className={styles.formRow}>
        <TextField
          label='Date of Incident'
          type='date'
          value={formData.incidentDate}
          onChange={(e) => handleChange('incidentDate', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: 1 }}
        />
        <div style={{ flex: 1 }}>
          <PrimaryDropdown
            label='Incident Type'
            defaultValue={formData.incidentType}
            onChange={(value) => handleChange('incidentType', value)}
            items={incidentTypeItems}
          />
        </div>
        <div style={{ flex: 1 }}>
          <PrimaryDropdown
            label='Severity'
            defaultValue={formData.severityOfIncident}
            onChange={(value) => handleChange('severityOfIncident', value)}
            items={severityItems}
          />
        </div>
      </div>

      <div className={styles.formRow}>
        <div style={{ flex: 1 }}>
          <PrimaryDropdown
            label='Review Channel'
            defaultValue={formData.reviewChannel}
            onChange={(value) => handleChange('reviewChannel', value)}
            items={reviewChannelItems}
          />
        </div>
        <TextField
          label='Review Score'
          type='number'
          value={formData.reviewScore ?? ''}
          onChange={(e) =>
            handleChange(
              'reviewScore',
              e.target.value ? Number(e.target.value) : null,
            )
          }
          sx={{ flex: 1 }}
        />
        <TextField
          label='Cost ($)'
          type='number'
          inputProps={{ step: '0.01' }}
          value={formData.cost ?? ''}
          onChange={(e) =>
            handleChange('cost', e.target.value ? Number(e.target.value) : null)
          }
          sx={{ flex: 1 }}
        />
      </div>

      <div className={styles.formRow}>
        <TextField
          label='Customer Mileage at Incident'
          type='number'
          value={formData.customerMileageAtIncident ?? ''}
          onChange={(e) =>
            handleChange(
              'customerMileageAtIncident',
              e.target.value ? Number(e.target.value) : null,
            )
          }
          sx={{ flex: 1 }}
        />
        <TextField
          label='DT Claims Number'
          value={formData.dtClaimsNumber}
          onChange={(e) => handleChange('dtClaimsNumber', e.target.value)}
          sx={{ flex: 1 }}
        />
      </div>

      <div className={styles.formRow}>
        <PrimaryCheckbox
          label='Lost Time Injury (LTI)'
          checked={formData.isLostTimeInjury}
          onChange={(_, checked) =>
            handleCheckboxChange('isLostTimeInjury', checked)
          }
        />
        <PrimaryCheckbox
          label='Qualifying Defect'
          checked={formData.qualifyingDefects}
          onChange={(_, checked) =>
            handleCheckboxChange('qualifyingDefects', checked)
          }
        />
        <PrimaryCheckbox
          label='DT Claims'
          checked={formData.dtClaims}
          onChange={(_, checked) => handleCheckboxChange('dtClaims', checked)}
        />
        <PrimaryCheckbox
          label='Wheel Off / Potential Wheel Off'
          checked={formData.wheelOff}
          onChange={(_, checked) => handleCheckboxChange('wheelOff', checked)}
        />
      </div>

      <TextField
        label='Summary / Overview'
        value={formData.incidentOverviewAndResolution}
        onChange={(e) =>
          handleChange('incidentOverviewAndResolution', e.target.value)
        }
        multiline
        rows={6}
        fullWidth
      />
    </div>
  )
}

export default OverviewTab
